import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import type { FulfillmentType, OrderStatus } from '@pizza-roma/shared';
import { statusClass, statusLabel, typeLabel } from '../lib/format';
import { useI18n } from '../i18n';

export function Spinner() {
  const { t } = useI18n();
  return <div className="spinner" aria-label={t.common.loading} />;
}

export function PageLoading() {
  return (
    <div className="page-loading">
      <Spinner />
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: Error | null; onRetry?: () => void }) {
  const { t } = useI18n();
  if (!error) return null;
  return (
    <div className="alert alert-error row-between">
      <span>{error.message}</span>
      {onRetry && (
        <button className="btn btn-sm" onClick={onRetry}>
          {t.common.retry}
        </button>
      )}
    </div>
  );
}

export function StatusBadge({ status }: { status: OrderStatus }) {
  useI18n(); // re-render on language change
  return (
    <span className={`badge ${statusClass(status)}`} data-status={status}>
      {statusLabel(status)}
    </span>
  );
}

export function TypeBadge({ type }: { type: FulfillmentType }) {
  useI18n();
  return <span className={`badge no-dot ${type === 'DELIVERY' ? 'b-type-delivery' : 'b-type-pickup'}`}>{typeLabel(type)}</span>;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  size,
  testId,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'lg';
  testId?: string;
}) {
  const { t } = useI18n();
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', k);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size === 'lg' ? 'modal-lg' : ''}`} role="dialog" aria-modal="true" data-testid={testId}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="btn btn-ghost icon-btn" onClick={onClose} aria-label={t.common.close}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Drawer({ onClose, children, testId }: { onClose: () => void; children: ReactNode; testId?: string }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="overlay drawer-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" data-testid={testId}>
        {children}
      </aside>
    </div>
  );
}

export function Empty({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <div>{children}</div>
    </div>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <label className="switch" title={label}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
      <span />
    </label>
  );
}

export function FoodImg({ src, alt }: { src: string | null | undefined; alt: string }) {
  return <img src={src || '/food/pizza-margherita.svg'} alt={alt} loading="lazy" />;
}
